import { useEffect, useRef } from 'react';

const values = [
  {
    number: '01',
    title: 'Enduring Craftsmanship',
    description: 'Every piece in our collection is built to last — from hand-joined solid wood joints to premium upholstery fabrics rated for 100,000+ rubs. We reject the disposable. We invest in the enduring.',
    icon: 'ri-hammer-line',
  },
  {
    number: '02',
    title: 'Thoughtful Curation',
    description: 'We don\'t carry everything. We carry the right things. Our buyers travel to source artisans and manufacturers who share our obsession with quality, proportion, and honest materials.',
    icon: 'ri-eye-line',
  },
  {
    number: '03',
    title: 'Personal Design Service',
    description: 'Every visit starts with a conversation. Our in-house design consultants help you navigate collections, plan layouts, and build a home that feels intentionally and authentically yours.',
    icon: 'ri-palette-line',
  },
  {
    number: '04',
    title: 'White-Glove Experience',
    description: 'From showroom to setup — your furniture arrives on your schedule, in perfect condition, assembled by our trained delivery team. No boxes, no stress, no damaged pieces.',
    icon: 'ri-star-line',
  },
];

export default function WhyEclectic() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="bg-dark py-28 overflow-hidden">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Why Choose Us</span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-white leading-tight mb-8">
              Why Eclectic<br />
              <em className="text-gold not-italic">Home?</em>
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-10">
              We believe your home deserves more than mass-produced furniture. 
              We bring the showroom experience to every interaction — expert guidance, uncompromising quality, delivered with care.
            </p>
            <div className="relative overflow-hidden rounded-2xl h-64">
              <img
                src="https://public.readdy.ai/ai/img_res/3ac0eaad322257cccab5527d25d1037f.jpg"
                alt="Eclectic Home furniture showroom Las Vegas"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/50 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="text-3xl font-serif font-bold text-white">20+</div>
                <div className="text-white/60 text-xs uppercase tracking-wider">Years in Business</div>
              </div>
            </div>
          </div>

          {/* Right - Values */}
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((val, i) => (
              <div
                key={val.title}
                style={{ marginTop: i % 2 === 1 ? '40px' : '0' }}
                className="bg-white/5 border border-white/8 rounded-2xl p-8 hover:bg-white/8 hover:border-gold/30 transition-all group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gold/10 group-hover:bg-gold/20 transition-colors">
                    <i className={`${val.icon} text-gold text-xl`} />
                  </div>
                  <span className="font-serif text-4xl font-bold text-white/8 group-hover:text-white/12 transition-colors">{val.number}</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-white mb-3">{val.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
